import type {
  AuthResponse, ChangePasswordInput, CreateTenantInput, CreateUserInput, EnterTenantResponse, LoginInput,
  PlatformAccessRecord, PlatformAuthResponse, PlatformLoginInput, PlatformTenantRecord, PlatformTenantSummary,
  PlatformUpdateTenantInput, PlatformUserRecord, PublicUser, RegisterInput,
  TenantRecord, UpdateTenantInput, UpdateUserInput, UserRecord,
} from '@hxiq/shared';
import { API_BASE_URL, isNativePlatform } from './platform';
import {
  impersonation, platformRefreshTokenStore, platformSessionHint, platformTokenStore,
  refreshTokenStore, sessionHint, tokenStore,
} from './token-store';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly fieldErrors?: { field: string; message: string }[],
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Shared across concurrent 401s so a burst of requests triggers exactly one
 * rotation. Without this, parallel refreshes race and all but one present an
 * already-used token — which reuse detection correctly treats as theft and
 * logs the user out. This is the classic bug in refresh-token clients.
 */
let inFlightRefresh: Promise<boolean> | null = null;

/**
 * 'session': the refresh token is gone, back to the login screen.
 * 'impersonation': a shop session opened from the platform console ran out;
 * there was never a refresh token, so the console is where to go back to.
 */
export type AuthLostReason = 'session' | 'impersonation';

let onAuthLost: ((reason: AuthLostReason) => void) | null = null;
export function setAuthLostHandler(handler: (reason: AuthLostReason) => void): void {
  onAuthLost = handler;
}

async function parseError(response: Response): Promise<ApiError> {
  let message = response.statusText || 'Request failed';
  let fieldErrors: { field: string; message: string }[] | undefined;
  try {
    const body = await response.json();
    if (typeof body?.message === 'string') message = body.message;
    else if (Array.isArray(body?.message)) message = body.message.join(', ');
    if (Array.isArray(body?.errors)) fieldErrors = body.errors;
  } catch {
    /* non-JSON error body */
  }
  return new ApiError(message, response.status, fieldErrors);
}

async function rawRequest(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  // FormData sets its own content-type, including the multipart boundary the
  // server needs to parse it. Setting application/json here makes the server
  // try to JSON.parse a multipart body and reject it — which is why the
  // ingestion upload used to bypass this helper entirely.
  const isFormData = typeof FormData !== 'undefined' && init.body instanceof FormData;
  if (init.body && !isFormData && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }

  const token = tokenStore.get();
  if (token) headers.set('authorization', `Bearer ${token}`);
  if (isNativePlatform()) headers.set('x-client-platform', 'capacitor');

  return fetch(`${API_BASE_URL}${path}`, { ...init, headers, credentials: 'include' });
}

async function attemptRefresh(): Promise<boolean> {
  inFlightRefresh ??= (async () => {
    try {
      const response = await rawRequest('/auth/refresh', {
        method: 'POST',
        body: JSON.stringify({ refreshToken: refreshTokenStore.get() }),
      });
      if (!response.ok) return false;

      const data = (await response.json()) as AuthResponse;
      tokenStore.set(data.accessToken);
      refreshTokenStore.set(data.refreshToken);
      sessionHint.set(true);
      return true;
    } catch {
      return false;
    } finally {
      // Cleared on the next tick so all waiters observe the same result first.
      queueMicrotask(() => {
        inFlightRefresh = null;
      });
    }
  })();

  return inFlightRefresh;
}

/**
 * Performs a request with one token refresh and replay on 401.
 *
 * Shared by JSON and binary callers: an authenticated <img> or <iframe> cannot
 * carry a bearer token, so those have to fetch bytes through here and wrap
 * them in an object URL instead of pointing at the endpoint directly.
 */
export async function requestRaw(path: string, init: RequestInit = {}): Promise<Response> {
  let response = await rawRequest(path, init);

  if (response.status === 401 && !path.startsWith('/auth/refresh')) {
    // A session entered from the console has no refresh token to try, and
    // must not fall through to one a real tenant login may have left in the
    // cookie jar — that would quietly swap identities.
    if (impersonation.active()) {
      tokenStore.set(null);
      impersonation.set(false);
      onAuthLost?.('impersonation');
      throw await parseError(response);
    }
    if (await attemptRefresh()) {
      response = await rawRequest(path, init);
    } else {
      tokenStore.set(null);
      refreshTokenStore.set(undefined);
      sessionHint.set(false);
      onAuthLost?.('session');
      throw await parseError(response);
    }
  }

  if (!response.ok) throw await parseError(response);
  return response;
}

export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await requestRaw(path, init);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

function adoptSession(data: AuthResponse): AuthResponse {
  tokenStore.set(data.accessToken);
  refreshTokenStore.set(data.refreshToken);
  sessionHint.set(true);
  return data;
}

export const api = {
  login: async (input: LoginInput) =>
    adoptSession(await request<AuthResponse>('/auth/login', { method: 'POST', body: JSON.stringify(input) })),

  register: async (input: RegisterInput) =>
    adoptSession(await request<AuthResponse>('/auth/register', { method: 'POST', body: JSON.stringify(input) })),

  me: () => request<PublicUser>('/auth/me'),

  /** Public. Says whether a demo tenant exists, so the login page can offer it or not. */
  health: () => request<{ status: string; database: string; demo: boolean }>('/health'),

  /** Anonymous session on the sandbox tenant, for the public demo. */
  startDemo: async () =>
    adoptSession(await request<AuthResponse>('/auth/demo', { method: 'POST' })),

  logout: async () => {
    try {
      await request<void>('/auth/logout', {
        method: 'POST',
        body: JSON.stringify({ refreshToken: refreshTokenStore.get() }),
      });
    } finally {
      tokenStore.set(null);
      refreshTokenStore.set(undefined);
      sessionHint.set(false);
    }
  },

  /** Silent session restore on app load. Never throws. */
  restore: async (): Promise<PublicUser | null> => {
    // A reload inside a shop entered from the console: the memory-only token
    // is gone and there is no refresh token. Nothing to restore, and the
    // caller sends the person back to the console.
    if (impersonation.active()) return null;
    // Skip the network entirely for visitors who have never logged in here.
    if (!sessionHint.exists()) return null;
    if (!(await attemptRefresh())) return null;
    try {
      return await request<PublicUser>('/auth/me');
    } catch {
      return null;
    }
  },

  /**
   * "Enter this tenant" from the platform console: a short shop session with
   * no refresh token. It takes the place of any tenant session in memory;
   * the console's own session (platformTokenStore) is untouched.
   */
  adoptTenantSession: async (accessToken: string): Promise<PublicUser> => {
    tokenStore.set(accessToken);
    refreshTokenStore.set(undefined);
    impersonation.set(true);
    return request<PublicUser>('/auth/me');
  },

  /** Leave a shop entered from the console. Nothing to revoke: the token simply stops being used. */
  leaveTenant: (): void => {
    tokenStore.set(null);
    impersonation.set(false);
  },
};

// ── Catalog & inventory ─────────────────────────────────────────────────────

export interface Paged<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface CatalogListItem {
  id: string;
  name: string;
  description: string | null;
  companyPartNumber: string | null;
  manufacturerPartNumber: string | null;
  status: string;
  isLotTracked: boolean;
  averageCost: string | null;
  lastPurchaseCost: string | null;
  countClass: string | null;
  reorderPolicy: string;
  defaultBinMin: string | null;
  onHand: string;
  reserved: string;
  available: string;
  /**
   * Some owned balance is below its effective minimum — the location
   * override if set, else the catalog default. The reorder report's rule.
   */
  belowMin: boolean;
  category: { id: string; name: string; code: string } | null;
  manufacturer: { id: string; name: string } | null;
  stockUom: { id: string; code: string };
}

export interface StockLocation {
  balanceId: string;
  locationId: string;
  locationCode: string;
  locationPath: string;
  isPickable: boolean;
  lot: { id: string; lotNumber: string; heatNumber: string | null } | null;
  ownershipType: string;
  ownerCustomerId: string | null;
  ownerCustomer?: { name: string } | null;
  onHand: string;
  reserved: string;
  available: string;
}

export interface LookupItem extends CatalogListItem {
  totalOnHand: string;
  totalReserved: string;
  totalAvailable: string;
  locations: StockLocation[];
}

export interface LookupResult {
  kind: 'LOCATION' | 'CATALOG_ITEM' | 'JOB' | 'NONE';
  match?: LookupItem | JobSummary | LocationSummary;
  candidates?: LookupItem[];
  message?: string;
}

export interface JobSummary {
  id: string;
  projectNumber: string;
  name: string;
  customer?: { id: string; name: string } | null;
  status: string;
}

export interface LocationSummary {
  id: string;
  code: string;
  path: string;
  storeroom: { id: string; code: string; name: string };
}

export interface IssueResult {
  transactionId: string;
  jobIssueLineId: string;
  balanceAfter: string;
  unitCost: string;
  unitPrice: string;
  priceSource: string;
  explanation: string;
  drawnFromReservation: boolean;
}

export interface IssuedLine {
  id: string;
  catalogItem: {
    id: string;
    name: string;
    companyPartNumber: string | null;
    manufacturerPartNumber: string | null;
    stockUom: { code: string };
  };
  quantity: string;
  returned: string;
  returnable: string;
  unitCost: string;
  unitPrice: string;
  markupPct: string;
  marginPct: string;
  extendedPrice: string;
  priceSource: string;
  issuedAt: string;
}

export interface LedgerEntry {
  id: string;
  type: string;
  quantityDelta: string;
  unitCost: string | null;
  balanceAfter: string;
  averageCostAfter: string | null;
  postedAt: string;
  reasonCode: string | null;
  note: string | null;
  location: { id: string; code: string; path: string };
  lot: { id: string; lotNumber: string; heatNumber: string | null } | null;
  job: { id: string; projectNumber: string; name: string } | null;
  sourceDocument: {
    id: string;
    filename: string | null;
    documentType: string;
    documentNumber: string | null;
    documentDate: string | null;
    supplier: { id: string; name: string } | null;
    /** The newest review task for the document, which is what /app/inbox/:id opens. */
    taskId: string | null;
  } | null;
  extendedCost: string | null;
  reversalOfId: string | null;
}

export interface ReferenceData {
  categories: { id: string; name: string; code: string; path: string }[];
  uoms: { id: string; code: string; name: string }[];
  manufacturers: { id: string; name: string }[];
  suppliers: { id: string; name: string }[];
  storerooms: {
    id: string;
    code: string;
    name: string;
    type: string;
    locations: { id: string; code: string; path: string; kind: string; barcode: string | null }[];
  }[];
  customers: { id: string; name: string }[];
  /** Scalar per-tenant knobs, every default filled in server-side. */
  settings: { dohWarningDays: number };
  /** Active sites, default first. Most tenants have one. */
  sites: { id: string; code: string; name: string; isDefault: boolean; timeZone: string }[];
}

const qs = (params: Record<string, string | number | undefined>) => {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') search.set(k, String(v));
  }
  const s = search.toString();
  return s ? `?${s}` : '';
};

export const catalogApi = {
  search: (params: CatalogSearchParams) =>
    request<Paged<CatalogListItem>>(`/catalog/items${qs({ ...params, belowMin: params.belowMin ? 'true' : undefined })}`),
  get: (id: string) => request<CatalogItemDetail>(`/catalog/items/${id}`),
  setCompanyPartNumber: (id: string, companyPartNumber: string) =>
    request<{ companyPartNumber: string; changed: boolean; previousPartNumbers: string[] }>(
      `/catalog/items/${id}/company-part-number`,
      { method: 'PATCH', body: JSON.stringify({ companyPartNumber }) },
    ),
  partNumberExposure: (id: string) =>
    request<{ issueLines: number; jobs: number }>(`/catalog/items/${id}/part-number-exposure`),
  generatePartNumber: (id: string) =>
    request<{ companyPartNumber: string; generated: boolean }>(
      `/catalog/items/${id}/company-part-number`,
      { method: 'POST' },
    ),
};

export const referenceApi = {
  all: () => request<ReferenceData>('/reference'),
};

export const inventoryApi = {
  lookup: (code: string, storeroomId?: string | null) =>
    request<LookupResult>('/inventory/lookup', {
      method: 'POST',
      body: JSON.stringify({ code, storeroomId: storeroomId ?? null }),
    }),
  issue: (body: Record<string, unknown>) =>
    request<IssueResult>('/inventory/issue', { method: 'POST', body: JSON.stringify(body) }),
  receive: (body: Record<string, unknown>) =>
    request<{ transactionId: string; balanceAfter: string }>('/inventory/receive', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  returnToStock: (body: Record<string, unknown>) =>
    request<{ transactionId: string; disposition: string; returnedAtUnitCost: string; destination: string }>(
      '/inventory/return',
      { method: 'POST', body: JSON.stringify(body) },
    ),
  issuedTo: (jobId: string) => request<IssuedLine[]>(`/inventory/jobs/${jobId}/issued`),
  ledger: (catalogItemId: string, params: LedgerQuery = {}) =>
    request<Paged<LedgerEntry>>(`/inventory/ledger/${catalogItemId}${qs({ ...params })}`),
  reorder: () => request<Record<string, unknown>[]>('/inventory/reorder'),
};

// ── Phase 2/3 management endpoints ──────────────────────────────────────────

export interface EquipmentRow {
  id: string;
  serialNumber: string;
  name: string;
  type: string | null;
  customer?: { id: string; name: string } | null;
  _count?: { items: number; jobs: number };
}

export interface EquipmentLink {
  id: string;
  equipment: EquipmentRow;
  source: 'MANUAL' | 'DERIVED_FROM_ISSUE';
  confidence: string | null;
  observationCount: number;
  confirmedAt: string | null;
  isSuggestion: boolean;
}

export interface CycleCountRow {
  id: string;
  reference: string;
  status: string;
  isBlind: boolean;
  scopeNote: string | null;
  createdAt: string;
  postedAt: string | null;
  storeroom: { code: string; name: string };
  _count: { lines: number };
}

export interface CycleCountLineRow {
  id: string;
  catalogItem: {
    id: string;
    name: string;
    companyPartNumber: string | null;
    manufacturerPartNumber: string | null;
    stockUom: { code: string };
  };
  location: { id: string; code: string; path: string };
  lotId: string | null;
  /** Null while a blind count is in progress — withheld by the API. */
  expectedQuantity: string | null;
  countedQuantity: string | null;
  variance: string | null;
  recountRequested: boolean;
  note: string | null;
  countedAt: string | null;
}

export interface CycleCountDetail extends CycleCountRow {
  lines: CycleCountLineRow[];
}

export const equipmentApi = {
  list: (filter: { q?: string; customerId?: string } = {}) => {
    const params = new URLSearchParams();
    if (filter.q) params.set('q', filter.q);
    if (filter.customerId) params.set('customerId', filter.customerId);
    const qs = params.toString();
    return request<EquipmentRow[]>(`/equipment${qs ? `?${qs}` : ''}`);
  },
  create: (body: Record<string, unknown>) =>
    request<EquipmentRow>('/equipment', { method: 'POST', body: JSON.stringify(body) }),
  /** A serial or name corrected; a changed serial renames the unit's Drive folder. */
  update: (id: string, body: { serialNumber?: string; name?: string; type?: string | null; customerId?: string | null }) =>
    request<EquipmentRow & { driveFolder: string }>(`/equipment/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  rebuildDerived: () =>
    request<{ created: number; updated: number; examined: number }>('/equipment/rebuild-derived', {
      method: 'POST',
    }),
  forItem: (itemId: string) => request<EquipmentLink[]>(`/catalog/items/${itemId}/equipment`),
  link: (itemId: string, equipmentId: string) =>
    request<unknown>(`/catalog/items/${itemId}/equipment`, {
      method: 'POST',
      body: JSON.stringify({ equipmentId }),
    }),
  confirm: (linkId: string) =>
    request<unknown>(`/catalog/equipment-links/${linkId}/confirm`, { method: 'POST' }),
  unlink: (linkId: string) =>
    request<unknown>(`/catalog/equipment-links/${linkId}`, { method: 'DELETE' }),
};

export const countsApi = {
  list: () => request<CycleCountRow[]>('/counts'),
  get: (id: string) => request<CycleCountDetail>(`/counts/${id}`),
  create: (body: Record<string, unknown>) =>
    request<{ id: string; reference: string }>('/counts', { method: 'POST', body: JSON.stringify(body) }),
  record: (id: string, body: Record<string, unknown>) =>
    request<{ lineId: string; recountRequested: boolean; message: string | null }>(`/counts/${id}/lines`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  post: (id: string, body: Record<string, unknown>) =>
    request<{ reference: string; linesCounted: number; adjustmentsPosted: number; requiredApproval: boolean }>(
      `/counts/${id}/post`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
};

export const storeroomApi = {
  list: () => request<Record<string, unknown>[]>('/storerooms'),
  create: (body: Record<string, unknown>) =>
    request<Record<string, unknown>>('/storerooms', { method: 'POST', body: JSON.stringify(body) }),
  createLocation: (body: Record<string, unknown>) =>
    request<Record<string, unknown>>('/storerooms/locations', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  setLimits: (balanceId: string, binMin: string | null, binMax: string | null) =>
    request<Record<string, unknown>>(`/storerooms/balances/${balanceId}/limits`, {
      method: 'PATCH',
      body: JSON.stringify({ binMin, binMax }),
    }),
};

export const catalogWriteApi = {
  createCategory: (body: {
    name: string;
    code: string;
    specTemplate: { key: string; unit: string | null; required: boolean }[];
  }) =>
    request<{ id: string; name: string; code: string }>('/catalog/categories', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  removeCategory: (id: string) =>
    request<{ deleted: boolean }>(`/catalog/categories/${id}`, { method: 'DELETE' }),
  removeItem: (id: string) =>
    request<{ deleted: boolean }>(`/catalog/items/${id}`, { method: 'DELETE' }),
  createManufacturer: (name: string) =>
    request<{ id: string; name: string }>('/catalog/manufacturers', {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),
  create: (body: Record<string, unknown>) =>
    request<{ id: string }>('/catalog/items', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: string, body: Record<string, unknown>) =>
    request<{ id: string }>(`/catalog/items/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  addCrossReference: (id: string, body: { targetItemId: string; type: string; note?: string | null }) =>
    request<unknown>(`/catalog/items/${id}/cross-references`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  removeCrossReference: (id: string, refId: string) =>
    request<{ deleted: boolean }>(`/catalog/items/${id}/cross-references/${refId}`, { method: 'DELETE' }),
  /** Obsolete, reactivate: the one field, through the same PATCH. */
  setStatus: (id: string, status: 'DRAFT' | 'ACTIVE' | 'OBSOLETE' | 'SUPERSEDED') =>
    request<{ id: string; status: string }>(`/catalog/items/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) }),
  upsertSupplierItem: (id: string, body: Record<string, unknown>) =>
    request<unknown>(`/catalog/items/${id}/supplier-items`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  removeSupplierItem: (supplierItemId: string) =>
    request<unknown>(`/catalog/supplier-items/${supplierItemId}`, { method: 'DELETE' }),
};

export const inventoryWriteApi = {
  adjust: (body: Record<string, unknown>) =>
    request<{ transactionId: string; balanceAfter: string }>('/inventory/adjust', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  transfer: (body: Record<string, unknown>) =>
    request<{ outTransactionId: string; inTransactionId: string }>('/inventory/transfer', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};

// ── Ingestion ───────────────────────────────────────────────────────────────

export interface QueueTask {
  id: string;
  documentId: string;
  status: string;
  heldReason: string | null;
  duplicateOfDocumentId: string | null;
  createdAt: string;
  document: {
    id: string;
    filename: string | null;
    documentType: string;
    documentNumber: string | null;
    documentDate: string | null;
    pageCount: number | null;
    supplier: { id: string; name: string } | null;
    message: { fromEmail: string | null; subject: string | null; receivedAt: string } | null;
    _count: { extractedLines: number };
  };
}

export interface ReviewLine {
  id: string;
  lineNumber: number;
  rawPartNumber: string | null;
  rawDescription: string | null;
  rawQuantity: string | null;
  rawUom: string | null;
  rawUnitCost: string | null;
  quantity: string | null;
  uomCode: string | null;
  unitCost: string | null;
  matchedItemId: string | null;
  matchStrategy: string | null;
  matchConfidence: string | null;
  fieldConfidence: Record<string, number>;
  /**
   * Where each field was read from, as fractions of the page. Absent for
   * documents the extractor could not point at — a text file has no page.
   */
  sourceSpans?: Record<string, { page: number; bbox: number[] } | null>;
  matchedItem: {
    id: string;
    name: string;
    companyPartNumber: string | null;
    manufacturerPartNumber: string | null;
    stockUom: { code: string };
  } | null;
  candidates: {
    id: string;
    name: string;
    companyPartNumber: string | null;
    manufacturerPartNumber: string | null;
  }[];
}

export interface ReviewTask extends QueueTask {
  duplicateOf: {
    id: string;
    documentNumber: string | null;
    documentDate: string | null;
    filename: string | null;
    supplier: { name: string } | null;
  } | null;
  document: QueueTask['document'] & {
    mimeType: string;
    extractedText: string | null;
    extractedLines: ReviewLine[];
    extractionRuns: {
      model: string;
      promptVersion: string;
      inputTokens: number | null;
      outputTokens: number | null;
      latencyMs: number | null;
      succeeded: boolean;
      errorMessage: string | null;
      createdAt: string;
    }[];
  };
}

export interface IngestResponse {
  documentId: string;
  status: string;
  duplicateOf?: string | null;
  taskId?: string | null;
  heldReason?: string | null;
  autoApplied: boolean;
  linesExtracted: number;
  linesMatched: number;
}

export const ingestionApi = {
  queue: (status?: string) =>
    request<QueueTask[]>(`/ingestion/queue${status ? `?status=${status}` : ''}`),
  task: (id: string) => request<ReviewTask>(`/ingestion/tasks/${id}`),
  approve: (id: string, body: Record<string, unknown>) =>
    request<{ posted: number; skipped: number }>(`/ingestion/tasks/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  suggestForLine: (taskId: string, lineId: string) =>
    request<{
      categoryId: string | null;
      categoryConfidence: number;
      categoryReason: string | null;
      proposedCategory: {
        name: string;
        code: string;
        specTemplate: { key: string; unit: string | null; required: boolean }[];
      } | null;
      name: string | null;
      specs: { key: string; value: string; unit: string | null; confidence: number }[];
      isLotTracked: boolean;
      lotTrackingReason: string | null;
      unavailableReason?: string;
    }>(`/ingestion/tasks/${taskId}/lines/${lineId}/suggest`, { method: 'POST' }),

  suggestSpecs: (body: { description: string; partNumber?: string | null; categoryId?: string | null }) =>
    request<{
      name: string | null;
      specs: { key: string; value: string; unit: string | null; confidence: number }[];
      unavailableReason?: string;
    }>('/ingestion/suggest-specs', { method: 'POST', body: JSON.stringify(body) }),

  createItemFromLine: (taskId: string, lineId: string, body: Record<string, unknown>) =>
    request<{
      catalogItemId: string;
      name: string;
      companyPartNumber: string | null;
      supplierMapped: boolean;
    }>(`/ingestion/tasks/${taskId}/lines/${lineId}/catalog-item`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  assignSupplier: (id: string, body: Record<string, unknown>) =>
    request<{ supplierId: string; rematched: number; duplicateOfDocumentId: string | null }>(
      `/ingestion/tasks/${id}/supplier`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  reject: (id: string, reason: string) =>
    request<{ rejected: boolean }>(`/ingestion/tasks/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  /** Multipart, so it bypasses the JSON request helper. */
  upload: async (file: File): Promise<IngestResponse> => {
    const form = new FormData();
    form.append('file', file);
    const headers = new Headers();
    const token = tokenStore.get();
    if (token) headers.set('authorization', `Bearer ${token}`);
    if (isNativePlatform()) headers.set('x-client-platform', 'capacitor');

    const response = await fetch(`${API_BASE_URL}/ingestion/upload`, {
      method: 'POST',
      headers,
      body: form,
      credentials: 'include',
    });
    if (!response.ok) throw await parseError(response);
    return (await response.json()) as IngestResponse;
  },

  /**
   * Fetches the stored document as a Blob.
   *
   * The endpoint requires a bearer token, and the browser does not attach
   * Authorization to <img>/<iframe> subresource loads — nor is there a cookie
   * to fall back on, because access tokens are memory-only by design. So the
   * bytes are fetched here and handed to the viewer as an object URL.
   */
  documentBlob: async (documentId: string): Promise<Blob> => {
    const response = await requestRaw(`/ingestion/documents/${documentId}/file`);
    return response.blob();
  },
};

// ── Jobs ────────────────────────────────────────────────────────────────────

export interface JobStage {
  id: string;
  name: string;
  position: number;
  isHydro: boolean;
  isTerminal: boolean;
  /** False for a waiting state (arrival, staged): no percentage, no progress bar. */
  hasProgress: boolean;
  instructions: string | null;
  checklist: string[] | null;
  minPhotos: number;
}

export interface JobHold {
  id: string;
  kind: 'QC' | 'GENERAL';
  reason: string;
  appliedAt: string;
  clearedAt: string | null;
  clearedNote: string | null;
}

/**
 * The order a job belongs to: one customer, one PO, one invoice, one or more
 * units. Billing lives here; the job carries the work.
 */
export interface JobOrderRef {
  id: string;
  /** Never blank — a PENDING-<project number> placeholder until the real one arrives. */
  poNumber: string;
  poPending: boolean;
  _count: { jobs: number };
}

export interface JobListItem {
  id: string;
  projectNumber: string;
  name: string;
  arrivalDate: string | null;
  shippedDate: string | null;
  status: 'OPEN' | 'CLOSED' | 'CANCELLED';
  driveFolderUrl: string | null;
  order: JobOrderRef;
  bolNumber: string | null;
  customer: { id: string; name: string } | null;
  equipment: { id: string; serialNumber: string; name: string } | null;
  stage: JobStage | null;
  site: { id: string; code: string; name: string } | null;
  holds: JobHold[];
  /** Derived server-side from the unit's job history; 1 for a unit never seen before. */
  visitNumber: number;
}

export interface JobListFilter {
  q?: string;
  stageId?: string;
  customerId?: string;
  status?: 'OPEN' | 'CLOSED' | 'CANCELLED';
  held?: boolean;
  /** 1-based; 50 per page unless asked otherwise. */
  page?: number;
  pageSize?: number;
}

export interface JobListPage {
  items: JobListItem[];
  total: number;
  page: number;
  pageSize: number;
  /** Per stage, over the whole filtered set — not just this page. Null is a job with no stage. */
  stageCounts: { stageId: string | null; count: number }[];
}

export interface JobDocument {
  id: string;
  kind: 'PO' | 'BOL' | 'CHANGE_ORDER' | 'INVOICE' | 'FINAL_REPORT' | 'OTHER';
  filename: string;
  contentType: string;
  byteSize: number;
  driveFileUrl: string | null;
  createdAt: string;
}

export interface HydroResult {
  id: string;
  result: 'PASS' | 'FAIL' | 'NOT_TESTED';
  testPressure: string | null;
  pressureUom: string | null;
  heldForMinutes: number | null;
  notes: string | null;
  testedAt: string;
  stageId: string;
}

export interface JobDetail extends JobListItem {
  visitNumber: number;
  /** Null until a charge sheet exists; approvedAt set once a manager finalized it. */
  chargeSheet: { approvedAt: string | null } | null;
  invoiceNumber: string | null;
  hydroResults: HydroResult[];
  progress: { stageId: string; percent: number; stage: { name: string; position: number } }[];
  /** What the customer reads in the portal: when it is expected back, and the shop's one line. */
  promisedDate: string | null;
  customerStatus: string | null;
  customerStatusAt: string | null;
}

export const jobsApi = {
  stages: () => request<JobStage[]>('/jobs/stages'),
  /** The tenant's guidance for a stage: shown when it is started. */
  updateStage: (id: string, input: { instructions?: string | null; checklist?: string[]; minPhotos?: number }) =>
    request<JobStage>(`/jobs/stages/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  list: (filter: JobListFilter = {}) => {
    const params = new URLSearchParams();
    if (filter.q) params.set('q', filter.q);
    if (filter.stageId) params.set('stageId', filter.stageId);
    if (filter.customerId) params.set('customerId', filter.customerId);
    if (filter.status) params.set('status', filter.status);
    if (filter.held !== undefined) params.set('held', String(filter.held));
    if (filter.page) params.set('page', String(filter.page));
    if (filter.pageSize) params.set('pageSize', String(filter.pageSize));
    const qs = params.toString();
    return request<JobListPage>(`/jobs${qs ? `?${qs}` : ''}`);
  },
  get: (id: string) => request<JobDetail>(`/jobs/${id}`),

  /**
   * Onboarding: one order, one job per unit. Returns the jobs in the order
   * the units were given, numbered consecutively.
   */
  create: (input: {
    customerId: string;
    arrivalDate: string;
    siteId?: string | null;
    units: { equipmentId?: string | null; serialNumber?: string | null; name?: string | null }[];
    poNumber?: string | null;
    poPending?: boolean;
    bolNumber?: string | null;
  }) => request<JobDetail[]>('/jobs', { method: 'POST', body: JSON.stringify(input) }),

  /** Corrections after onboarding. The project number never changes. */
  update: (id: string, input: {
    name?: string; arrivalDate?: string; customerId?: string; equipmentId?: string | null;
    serialNumber?: string | null; siteId?: string; bolNumber?: string | null;
  }) => request<JobDetail>(`/jobs/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),

  /** The 4×6 tag: QR code and job number, as a PNG to print or show. */
  tagUrl: (id: string) => `${API_BASE_URL}/jobs/${id}/tag.png`,
  tagBlob: async (id: string): Promise<Blob> => (await requestRaw(`/jobs/${id}/tag.png`)).blob(),

  documents: (jobId: string) => request<JobDocument[]>(`/jobs/${jobId}/documents`),

  /** Multipart. The file lands in the job's Docs folder. */
  attachDocument: async (jobId: string, kind: JobDocument['kind'], file: File): Promise<JobDocument> => {
    const form = new FormData();
    form.append('file', file);
    form.append('kind', kind);
    return request<JobDocument>(`/jobs/${jobId}/documents`, { method: 'POST', body: form });
  },

  /** Relative to the API: pass to openProtectedFile — a plain href gets a 401. */
  documentFilePath: (id: string) => `/documents/${id}/file`,

  advanceStage: (id: string, input: { stageId: string; percent?: number }) =>
    request<JobDetail>(`/jobs/${id}/stage`, { method: 'POST', body: JSON.stringify(input) }),

  applyHold: (id: string, input: { kind: 'QC' | 'GENERAL'; reason: string }) =>
    request<JobHold>(`/jobs/${id}/holds`, { method: 'POST', body: JSON.stringify(input) }),

  clearHold: (id: string, holdId: string, note?: string) =>
    request<JobHold>(`/jobs/${id}/holds/${holdId}/clear`, {
      method: 'POST',
      body: JSON.stringify({ note: note ?? null }),
    }),

  recordHydro: (
    id: string,
    input: {
      stageId: string;
      result: 'PASS' | 'FAIL' | 'NOT_TESTED';
      testPressure?: string | null;
      pressureUom?: string | null;
      heldForMinutes?: number | null;
      notes?: string | null;
    },
  ) => request<HydroResult>(`/jobs/${id}/hydro`, { method: 'POST', body: JSON.stringify(input) }),
};

// ── The stage runner ────────────────────────────────────────────────────────

export type StageState = 'LOCKED' | 'READY' | 'IN_PROGRESS' | 'COMPLETE' | 'SKIPPED';

export interface RunnerStage {
  id: string;
  name: string;
  position: number;
  isHydro: boolean;
  isTerminal: boolean;
  instructions: string | null;
  checklist: string[];
  minPhotos: number;
  /** Completing asks when the next stage is scheduled to start. */
  schedulesNext: boolean;
  photoCount: number;
  state: StageState;
  /** Why it is LOCKED — empty otherwise. */
  blockers: string[];
  startedAt: string | null;
  startedBy: string | null;
  completedAt: string | null;
  completedBy: string | null;
  skippedAt: string | null;
  skipNote: string | null;
  note: string | null;
}

export interface TodoItem {
  key: string;
  text: string;
  href: string;
  severity: 'blocking' | 'warning' | 'info';
}

export interface RunnerView {
  jobId: string;
  status: 'ONBOARDING' | 'IN_PROGRESS' | 'COMPLETE';
  stages: RunnerStage[];
  todo: TodoItem[];
}

/** A manager starts and completes each stage; every gate that refuses says why. */
export const runnerApi = {
  view: (jobId: string) => request<RunnerView>(`/jobs/${jobId}/runner`),
  start: (jobId: string, stageId: string) =>
    request<RunnerView>(`/jobs/${jobId}/stages/${stageId}/start`, { method: 'POST', body: '{}' }),
  complete: (jobId: string, stageId: string, note?: string | null) =>
    request<RunnerView>(`/jobs/${jobId}/stages/${stageId}/complete`, { method: 'POST', body: JSON.stringify({ note: note ?? null }) }),
  skip: (jobId: string, stageId: string, note: string) =>
    request<RunnerView>(`/jobs/${jobId}/stages/${stageId}/skip`, { method: 'POST', body: JSON.stringify({ note }) }),
  reopen: (jobId: string, stageId: string, note: string) =>
    request<RunnerView>(`/jobs/${jobId}/stages/${stageId}/reopen`, { method: 'POST', body: JSON.stringify({ note }) }),
  resolveHydro: (jobId: string, stageId: string, input: { action: 'RESTART_AT' | 'RETEST' | 'SKIP'; restartStageId?: string | null; note: string }) =>
    request<RunnerView>(`/jobs/${jobId}/stages/${stageId}/resolve-hydro`, { method: 'POST', body: JSON.stringify(input) }),
};

// ── Orders ──────────────────────────────────────────────────────────────────

export type BillingStatus = 'IN_PROGRESS' | 'READY_TO_INVOICE' | 'ON_HOLD' | 'INVOICED' | 'PAID';

export interface OrderAuthorization {
  id: string;
  kind: 'PO' | 'CHANGE_ORDER';
  sequence: number;
  number: string;
  issuedDate: string | null;
  amount: string | null;
  description: string | null;
  coversTimeAndMaterial: boolean;
  filename: string | null;
  byteSize: number | null;
  createdAt: string;
}

export interface OrderDetail {
  id: string;
  poNumber: string;
  poPending: boolean;
  invoiceNumber: string | null;
  invoicedAt: string | null;
  invoiceTotal: string | null;
  invoiceStoragePath: string | null;
  paidAt: string | null;
  paidAmount: string | null;
  paymentMethod: string | null;
  paymentReference: string | null;
  billingHoldReason: string | null;
  billingHoldAt: string | null;
  customer: { id: string; name: string; netTermsDays: number | null; contacts: { id: string; name: string; email: string | null; phone: string | null; tags: string[] }[] };
  authorizations: OrderAuthorization[];
  poLineItems: { id: string; description: string; amount: string; coversTimeAndMaterial: boolean }[];
  jobs: {
    id: string; projectNumber: string; name: string; status: JobListItem['status']; arrivalDate: string | null;
    equipment: { serialNumber: string } | null;
    stage: { name: string; isTerminal: boolean } | null;
    chargeSheet: { approvedAt: string | null } | null;
    _count: { chargeLines: number };
  }[];
  /** Derived, never stored. */
  billingStatus: BillingStatus;
  authorizedTotal: number;
  quotedTotal: number;
  unitsComplete: number;
  sheetsFinalized: number;
}

export const ordersApi = {
  get: (id: string) => request<OrderDetail>(`/orders/${id}`),
  /** The bookkeeper's queue. */
  readyToInvoice: () => request<OrderDetail[]>('/orders/ready-to-invoice'),
  /** Everything not yet paid, with billing status. */
  open: () => request<OrderDetail[]>('/orders/open'),
  setPo: (id: string, poNumber: string) =>
    request<OrderDetail>(`/orders/${id}/po`, { method: 'PATCH', body: JSON.stringify({ poNumber }) }),
  /** Multipart: the PO or a change order, with its document. */
  addAuthorization: (
    id: string,
    input: { kind: 'PO' | 'CHANGE_ORDER'; number: string; issuedDate?: string | null; amount?: string | null; description?: string | null; coversTimeAndMaterial?: boolean },
    file?: File | null,
  ) => {
    const form = new FormData();
    form.append('kind', input.kind);
    form.append('number', input.number);
    if (input.issuedDate) form.append('issuedDate', input.issuedDate);
    if (input.amount) form.append('amount', input.amount);
    if (input.description) form.append('description', input.description);
    if (input.coversTimeAndMaterial) form.append('coversTimeAndMaterial', 'true');
    if (file) form.append('file', file);
    return request<OrderDetail>(`/orders/${id}/authorizations`, { method: 'POST', body: form });
  },
  /** Relative to the API: pass to openProtectedFile — a plain href gets a 401. */
  authorizationFilePath: (orderId: string, authorizationId: string) =>
    `/orders/${orderId}/authorizations/${authorizationId}/file`,
  /** What the invoice will say and the number it will get. */
  invoicePreview: (id: string) =>
    request<{ invoiceNumber: string; total: number; units: { projectNumber: string; subtotal: number; lines: number }[]; poLines: { description: string; amount: number }[] }>(`/orders/${id}/invoice-preview`),
  /** Generates the invoice PDF; a blank number takes the shop's next. */
  invoice: (id: string, input: { invoiceNumber?: string | null; invoicedAt: string; terms?: string | null }) =>
    request<OrderDetail>(`/orders/${id}/invoice`, { method: 'POST', body: JSON.stringify(input) }),
  /** Relative to the API: pass to openProtectedFile. */
  invoicePdfPath: (id: string) => `/orders/${id}/invoice.pdf`,
  paid: (id: string, input: { paidAt: string; amount?: string | null; method?: string | null; reference?: string | null }) =>
    request<OrderDetail>(`/orders/${id}/paid`, { method: 'POST', body: JSON.stringify(input) }),
  setHold: (id: string, reason: string | null) =>
    request<OrderDetail>(`/orders/${id}/hold`, { method: 'POST', body: JSON.stringify({ reason }) }),
};

// ── Requisitions ────────────────────────────────────────────────────────────

export interface RequisitionLine {
  id: string;
  quantityRequested: string;
  quantityIssued: string;
  quantityOutstanding: string;
  status:
    | 'REQUESTED'
    | 'PARTIALLY_FILLED'
    | 'WAITING_ON_PARTS'
    | 'PARTS_RECEIVED'
    | 'CLOSED_SHORT'
    | 'FILLED';
  expectedDate: string | null;
  remainderNote: string | null;
  catalogItem: { id: string; name: string; companyPartNumber: string | null };
}

export interface Requisition {
  id: string;
  createdAt: string;
  note: string | null;
  status: RequisitionLine['status'];
  isClosed: boolean;
  lines: RequisitionLine[];
}

/** A queue row: a requisition with the job it belongs to and who asked. */
export interface QueuedRequisition extends Requisition {
  job: {
    id: string;
    projectNumber: string;
    name: string;
    customer: { name: string } | null;
    equipment: { serialNumber: string } | null;
    stage: { name: string } | null;
  };
  raisedBy: { firstName: string; lastName: string };
}

export const requisitionsApi = {
  forJob: (jobId: string) => request<Requisition[]>(`/requisitions/job/${jobId}`),

  /** The storeroom's queue across every open job. */
  queue: (includeSettled = false) =>
    request<QueuedRequisition[]>(`/requisitions/queue${includeSettled ? '?includeSettled=true' : ''}`),

  raise: (input: {
    jobId: string;
    note?: string | null;
    lines: { catalogItemId: string; quantity: string; locationId?: string | null }[];
  }) => request<Requisition>('/requisitions', { method: 'POST', body: JSON.stringify(input) }),

  fulfil: (
    lineId: string,
    input: { quantity: string; locationId: string; catalogItemId?: string | null },
  ) =>
    request<RequisitionLine>(`/requisitions/lines/${lineId}/fulfil`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  disposeRemainder: (
    lineId: string,
    input: { disposition: 'ON_ORDER' | 'CLOSED_SHORT'; expectedDate?: string | null; note?: string | null },
  ) =>
    request<RequisitionLine>(`/requisitions/lines/${lineId}/remainder`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
};

// ── Billing ─────────────────────────────────────────────────────────────────

export interface ChargeLine {
  id: string;
  kind: 'MATERIAL' | 'LABOR' | 'SERVICE' | 'MANUAL';
  description: string;
  cost: string;
  billAmount: string;
  disposition: 'BILL' | 'COVERED_BY_PO' | 'NOT_BILLED';
  dispositionNote: string | null;
  hours: string | null;
  costRate: string | null;
  billRate: string | null;
  serviceId: string | null;
  service: { id: string; name: string } | null;
  /** Set when the line came from an issue: its cost is frozen and it cannot be deleted. */
  jobIssueLineId: string | null;
}

export interface ChargeTotals {
  cost: number;
  revenue: number;
  margin: number;
  marginPct: number | null;
  absorbed: number;
}

export interface ChargeSheet {
  job: { id: string; projectNumber: string; name: string; customer: { name: string } | null };
  lines: ChargeLine[];
  poLineItems: { id: string; description: string; amount: string; coversTimeAndMaterial: boolean }[];
  totals: ChargeTotals;
  approvedAt: string | null;
  /** The order's — one PO for every unit on it. */
  poNumber: string;
  poPending: boolean;
  unitsOnOrder: number;
}

export const billingApi = {
  sheet: (jobId: string) => request<ChargeSheet>(`/jobs/${jobId}/charges`),
  services: () => request<{ id: string; name: string; description: string | null }[]>('/services'),

  addCharge: (
    jobId: string,
    input: {
      kind: ChargeLine['kind'];
      description: string;
      cost: string;
      billAmount: string;
      disposition?: ChargeLine['disposition'];
      dispositionNote?: string | null;
      serviceId?: string | null;
      hours?: string | null;
      costRate?: string | null;
      billRate?: string | null;
    },
  ) => request<ChargeLine>(`/jobs/${jobId}/charges`, { method: 'POST', body: JSON.stringify(input) }),

  setDisposition: (lineId: string, input: { disposition: ChargeLine['disposition']; note?: string | null }) =>
    request<ChargeLine>(`/charges/${lineId}/disposition`, { method: 'POST', body: JSON.stringify(input) }),

  addPoLine: (
    jobId: string,
    input: { poNumber: string; description: string; amount: string; coversTimeAndMaterial?: boolean },
  ) => request(`/jobs/${jobId}/po-lines`, { method: 'POST', body: JSON.stringify(input) }),

  coverByPo: (jobId: string) =>
    request<{ covered: number }>(`/jobs/${jobId}/charges/cover-by-po`, { method: 'POST', body: '{}' }),

  approve: (jobId: string) =>
    request(`/jobs/${jobId}/charges/approve`, { method: 'POST', body: '{}' }),

  /**
   * `COMPANY` carries cost and margin; `CUSTOMER` is generated without them.
   * The distinction is structural, not a display toggle — see
   * docs/features/charge-sheet.md.
   */
  report: (jobId: string, audience: 'COMPANY' | 'CUSTOMER') =>
    request<Record<string, unknown>>(`/jobs/${jobId}/charge-report?audience=${audience}`),
};

// ── Activity ────────────────────────────────────────────────────────────────

export interface ActivityEntry {
  id: string;
  kind: 'REMARK' | 'ACTIVITY';
  event: string | null;
  body: string;
  actorId: string;
  actor: { id: string; firstName: string; lastName: string };
  editedAt: string | null;
  createdAt: string;
}

export const activityApi = {
  forJob: (jobId: string, filter: { kind?: string; q?: string; actorId?: string; from?: string; to?: string } = {}) => {
    const params = new URLSearchParams();
    if (filter.kind) params.set('kind', filter.kind);
    if (filter.q) params.set('q', filter.q);
    if (filter.actorId) params.set('actorId', filter.actorId);
    // Calendar days, YYYY-MM-DD, inclusive at both ends.
    if (filter.from) params.set('from', filter.from);
    if (filter.to) params.set('to', filter.to);
    const qs = params.toString();
    return request<ActivityEntry[]>(`/jobs/${jobId}/activity${qs ? `?${qs}` : ''}`);
  },

  addRemark: (jobId: string, body: string) =>
    request<ActivityEntry>(`/jobs/${jobId}/remarks`, { method: 'POST', body: JSON.stringify({ body }) }),

  search: (q: string) =>
    request<(ActivityEntry & { job: { id: string; projectNumber: string; name: string } })[]>(
      `/activity/search?q=${encodeURIComponent(q)}`,
    ),
};

// ── Photos ──────────────────────────────────────────────────────────────────

export interface CaptionTemplate {
  id: string;
  text: string;
  stageId: string | null;
  position: number;
}

export interface PhotoGallery {
  total: number;
  stages: {
    stage: string;
    position: number;
    photos: {
      id: string;
      caption: string | null;
      takenAt: string | null;
      hasLocation: boolean;
      byteSize: number;
      contentType: string;
      /** Set when the customer sees it in the portal. */
      sharedAt: string | null;
    }[];
  }[];
}

export const photosApi = {
  gallery: (jobId: string) => request<PhotoGallery>(`/jobs/${jobId}/photos`),
  templates: (stageId?: string) =>
    request<CaptionTemplate[]>(`/caption-templates${stageId ? `?stageId=${stageId}` : ''}`),

  capture: async (
    jobId: string,
    file: File,
    meta: { stageId?: string; captionTemplateId?: string; takenAt?: string },
  ) => {
    const form = new FormData();
    form.append('file', file);
    for (const [k, v] of Object.entries(meta)) if (v) form.append(k, v);
    const response = await requestRaw(`/jobs/${jobId}/photos`, { method: 'POST', body: form });
    return response.json();
  },

  /**
   * Same reason as documentBlob: the browser does not attach Authorization to
   * an <img> subresource load, and access tokens are memory-only by design.
   */
  blob: async (photoId: string): Promise<Blob> => {
    const response = await requestRaw(`/photos/${photoId}/file`);
    return response.blob();
  },
};

// ── Settings: roles, notification preferences, customers ────────────────────

export interface RoleRow {
  id: string;
  name: string;
  description: string | null;
  isAdmin: boolean;
  isSystem: boolean;
  requiresMfa: boolean;
  userCount: number;
  permissions: string[];
}

export const rolesApi = {
  vocabulary: () => request<string[]>('/permissions'),
  list: () => request<RoleRow[]>('/roles'),
  create: (input: { name: string; description?: string | null; permissions: string[] }) =>
    request<RoleRow>('/roles', { method: 'POST', body: JSON.stringify(input) }),
  update: (
    id: string,
    input: { name?: string; description?: string | null; permissions?: string[]; requiresMfa?: boolean },
  ) => request<RoleRow>(`/roles/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  remove: (id: string) => request<{ deleted: boolean }>(`/roles/${id}`, { method: 'DELETE' }),
};

export interface MySubscriptions {
  phone: string | null;
  roleName: string | null;
  subscriptions: { event: string; channel: 'EMAIL' | 'SMS'; enabled: boolean; source: string }[];
}

export const notificationsApi = {
  mine: () => request<MySubscriptions>('/notifications/me'),
  /** `null` clears the override, putting you back on your role's default. */
  set: (event: string, channel: 'EMAIL' | 'SMS', enabled: boolean | null) =>
    request('/notifications/me', { method: 'POST', body: JSON.stringify({ event, channel, enabled }) }),
};

export interface CustomerContactRow {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  role: string | null;
  /** What this person is for: AP receives invoices, SERVICE quotes and change orders. */
  tags: string[];
  notes: string | null;
  isPrimary: boolean;
}

export interface CustomerRow {
  id: string;
  name: string;
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
  netTermsDays: number | null;
  notes: string | null;
  contacts: CustomerContactRow[];
  _count?: { jobs: number; equipment: number; orders?: number };
}

export interface CustomerDetail extends CustomerRow {
  jobs: { id: string; projectNumber: string; name: string; status: string; arrivalDate: string | null; stage: { name: string } | null; equipment: { serialNumber: string } | null }[];
}

export interface CustomerDocumentRow {
  id: string; kind: string; title: string; filename: string; contentType: string; byteSize: number;
  driveFileUrl: string | null; effectiveAt: string | null; expiresAt: string | null; notes: string | null; createdAt: string;
  uploadedBy: { firstName: string; lastName: string };
}

export interface CustomerInput {
  name: string;
  address1?: string | null; address2?: string | null; city?: string | null; state?: string | null;
  postalCode?: string | null; country?: string | null; netTermsDays?: number | null; notes?: string | null;
}

export interface ContactInput {
  name: string; email?: string | null; phone?: string | null; mobile?: string | null; role?: string | null;
  tags?: string[]; notes?: string | null; isPrimary?: boolean;
}

export interface SiteRow {
  id: string;
  code: string;
  name: string;
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  timeZone: string;
  isDefault: boolean;
  isActive: boolean;
  _count: { storerooms: number; jobs: number };
}

export type SiteInput = {
  code: string; name: string;
  address1?: string | null; address2?: string | null; city?: string | null;
  state?: string | null; postalCode?: string | null; timeZone?: string; isDefault?: boolean;
};

export const sitesApi = {
  list: () => request<SiteRow[]>('/sites'),
  create: (input: SiteInput) => request<SiteRow>('/sites', { method: 'POST', body: JSON.stringify(input) }),
  update: (id: string, input: Partial<SiteInput> & { isActive?: boolean }) =>
    request<SiteRow>(`/sites/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
};

export const customersApi = {
  list: (q?: string) => request<CustomerRow[]>(`/customers${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  get: (id: string) => request<CustomerDetail>(`/customers/${id}`),
  create: (input: CustomerInput) => request<CustomerRow>('/customers', { method: 'POST', body: JSON.stringify(input) }),
  /** Renaming also renames the customer's Drive folder. */
  update: (id: string, input: CustomerInput) =>
    request<CustomerRow & { driveFolder: string }>(`/customers/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  addContact: (customerId: string, input: ContactInput) =>
    request<CustomerContactRow>(`/customers/${customerId}/contacts`, { method: 'POST', body: JSON.stringify(input) }),
  updateContact: (contactId: string, input: ContactInput) =>
    request<CustomerContactRow>(`/customers/contacts/${contactId}`, { method: 'PATCH', body: JSON.stringify(input) }),
  removeContact: (contactId: string) => request(`/customers/contacts/${contactId}`, { method: 'DELETE' }),

  documents: (customerId: string) => request<CustomerDocumentRow[]>(`/customers/${customerId}/documents`),
  /** Multipart. Mirrored to the customer's Docs folder in Drive. */
  addDocument: (customerId: string, file: File, meta: { kind?: string; title?: string; effectiveAt?: string; expiresAt?: string; notes?: string }) => {
    const form = new FormData();
    form.append('file', file);
    for (const [k, v] of Object.entries(meta)) if (v) form.append(k, v);
    return request<CustomerDocumentRow>(`/customers/${customerId}/documents`, { method: 'POST', body: form });
  },
  updateDocument: (id: string, meta: { kind?: string; title?: string; effectiveAt?: string | null; expiresAt?: string | null; notes?: string | null }) =>
    request<CustomerDocumentRow>(`/customers/documents/${id}`, { method: 'PATCH', body: JSON.stringify(meta) }),
  removeDocument: (id: string) => request(`/customers/documents/${id}`, { method: 'DELETE' }),
  /** Relative to the API: pass to openProtectedFile. */
  documentFilePath: (id: string) => `/customers/documents/${id}/file`,
};

// ── Customer mail ───────────────────────────────────────────────────────────

export interface MailboxRow {
  id: string; name: string; address: string; purpose: string; provider: 'IMAP' | 'GMAIL' | 'MICROSOFT365';
  visibility: 'SHARED' | 'OWNER'; ownerUserId: string | null;
  imapHost: string | null; imapPort: number | null; imapTls: boolean; smtpHost: string | null; smtpPort: number | null; smtpTls: boolean;
  username: string | null; inboxFolder: string; sentFolder: string; isActive: boolean; lastPolledAt: string | null; lastError: string | null;
  _count: { threads: number };
}
export interface MailboxInput {
  name: string; address: string; purpose?: string; visibility?: 'SHARED' | 'OWNER'; ownerUserId?: string | null;
  imapHost?: string | null; imapPort?: number | null; imapTls?: boolean; smtpHost?: string | null; smtpPort?: number | null; smtpTls?: boolean;
  username?: string | null; password?: string | null; inboxFolder?: string; sentFolder?: string; isActive?: boolean;
}
export interface MailLink {
  id: string; kind: 'JOB' | 'ORDER' | 'QUOTE'; confidence: 'CERTAIN' | 'SUGGESTED'; reason: string; confirmedAt: string | null;
  job: { id: string; projectNumber: string; name: string } | null;
  order: { id: string; poNumber: string; invoiceNumber: string | null } | null;
  quote: { id: string; number: string } | null;
}
export interface MailThreadRow {
  id: string; subject: string; lastMessageAt: string; messageCount: number; closedAt: string | null;
  customer: { id: string; name: string };
  mailbox: { id: string; name: string; address: string; purpose: string };
  links: MailLink[];
}
export interface MailMessageRow {
  id: string; direction: 'IN' | 'OUT'; fromEmail: string; fromName: string | null; toEmails: string[]; ccEmails: string[];
  subject: string; textBody: string; sentAt: string; sendError: string | null;
  sentBy: { firstName: string; lastName: string } | null;
  attachments: { id: string; filename: string; contentType: string; byteSize: number }[];
}
export interface MailThreadDetail extends MailThreadRow { messages: MailMessageRow[] }
export type MailLinkTarget = { kind: 'JOB' | 'ORDER' | 'QUOTE'; id: string };

export const mailApi = {
  mailboxes: () => request<MailboxRow[]>('/mailboxes'),
  createMailbox: (input: MailboxInput) => request<MailboxRow>('/mailboxes', { method: 'POST', body: JSON.stringify(input) }),
  updateMailbox: (id: string, input: MailboxInput) => request<MailboxRow>(`/mailboxes/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  removeMailbox: (id: string) => request(`/mailboxes/${id}`, { method: 'DELETE' }),
  testMailbox: (id: string) => request<{ imap: string; smtp: string }>(`/mailboxes/${id}/test`, { method: 'POST', body: '{}' }),
  poll: () => request<{ mailboxes: number; kept: number; skipped: number }>('/mailboxes/poll', { method: 'POST', body: '{}' }),

  threadsForCustomer: (customerId: string) => request<MailThreadRow[]>(`/customers/${customerId}/threads`),
  threadsForJob: (jobId: string) => request<MailThreadRow[]>(`/jobs/${jobId}/threads`),
  thread: (id: string) => request<MailThreadDetail>(`/threads/${id}`),
  attachmentPath: (id: string) => `/mail/attachments/${id}/file`,
  link: (threadId: string, target: MailLinkTarget) => request<MailThreadDetail>(`/threads/${threadId}/links`, { method: 'POST', body: JSON.stringify(target) }),
  confirmLink: (threadId: string, linkId: string) => request<MailThreadDetail>(`/threads/${threadId}/links/${linkId}/confirm`, { method: 'POST', body: '{}' }),
  unlink: (threadId: string, linkId: string) => request<MailThreadDetail>(`/threads/${threadId}/links/${linkId}`, { method: 'DELETE' }),
  setClosed: (threadId: string, closed: boolean) => request<MailThreadDetail>(`/threads/${threadId}/closed`, { method: 'PATCH', body: JSON.stringify({ closed }) }),

  /** Multipart: JSON in `payload`, files in `files`. */
  compose: (input: { mailboxId: string; customerId: string; to: string[]; cc?: string[]; subject: string; body: string; links?: MailLinkTarget[] }, files: File[] = []) => {
    const form = new FormData();
    form.append('payload', JSON.stringify(input));
    for (const f of files) form.append('files', f);
    return request<MailThreadDetail>('/mail/compose', { method: 'POST', body: form });
  },
  reply: (threadId: string, input: { body: string; to?: string[]; cc?: string[] }, files: File[] = []) => {
    const form = new FormData();
    form.append('payload', JSON.stringify(input));
    for (const f of files) form.append('files', f);
    return request<MailThreadDetail>(`/threads/${threadId}/reply`, { method: 'POST', body: form });
  },
  emailInvoice: (orderId: string, input: { mailboxId?: string; to?: string[]; message?: string }) =>
    request<MailThreadDetail>(`/orders/${orderId}/invoice/email`, { method: 'POST', body: JSON.stringify(input) }),
};

// ── Charge line edits, photo thumbnails and captions ────────────────────────

/** The full line as the sheet returns it; `ChargeLine` above is the subset the table used to need. */
/** The full line is what the sheet returns; the name stays for the callers that use it. */
export type ChargeLineDetail = ChargeLine;

export interface ChargeLineEdit {
  description?: string;
  cost?: string;
  billAmount?: string;
  hours?: string | null;
  costRate?: string | null;
  billRate?: string | null;
  serviceId?: string | null;
}

export const chargeLinesApi = {
  /** Refused once the sheet is approved; a material line takes only description and bill. */
  update: (lineId: string, input: ChargeLineEdit) =>
    request<ChargeLineDetail>(`/charges/${lineId}`, { method: 'PATCH', body: JSON.stringify(input) }),
  /** Refused once approved, and refused for material — that comes from issues. */
  remove: (lineId: string) =>
    request<{ deleted: boolean }>(`/charges/${lineId}`, { method: 'DELETE' }),
};

export const photoCaptionsApi = {
  /** The gallery tile: ~320px wide, made on first request for older photos. */
  thumb: async (photoId: string): Promise<Blob> => {
    const response = await requestRaw(`/photos/${photoId}/file?size=thumb`);
    return response.blob();
  },
  /** One tap for twelve photos of the same thing. */
  applyTemplate: (photoIds: string[], captionTemplateId: string) =>
    request<{ captioned: number }>('/photos/caption', { method: 'POST', body: JSON.stringify({ photoIds, captionTemplateId }) }),
  /** Free text for the one photo that needs it; replaces any template on it. Null clears. */
  setCaption: (photoId: string, captionText: string | null) =>
    request<{ id: string; captionText: string | null }>(`/photos/${photoId}/caption`, { method: 'PATCH', body: JSON.stringify({ captionText }) }),
};

// ── Settings: people, the shop record, your own account ─────────────────────

export const usersApi = {
  list: () => request<UserRecord[]>('/users'),
  /** The temporary password in the response is shown once and never again. */
  create: (input: CreateUserInput) =>
    request<{ user: UserRecord; temporaryPassword: string }>('/users', { method: 'POST', body: JSON.stringify(input) }),
  update: (id: string, input: UpdateUserInput) =>
    request<UserRecord>(`/users/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  resetPassword: (id: string) =>
    request<{ temporaryPassword: string }>(`/users/${id}/reset-password`, { method: 'POST', body: '{}' }),
};

export const tenantApi = {
  get: () => request<TenantRecord>('/tenant'),
  update: (input: UpdateTenantInput) =>
    request<TenantRecord>('/tenant', { method: 'PATCH', body: JSON.stringify(input) }),
};

export const accountApi = {
  /** Other sessions end; the one this is sent from stays signed in. */
  changePassword: (input: ChangePasswordInput) =>
    request<void>('/auth/password', {
      method: 'POST',
      // Native keeps its refresh token in the body; the browser's travels as a cookie.
      body: JSON.stringify({ ...input, refreshToken: refreshTokenStore.get() }),
    }),
};

// ── Settings: the pipeline, services, labour rates, storerooms, assets, defaults ──
//
// Everything a new tenant is seeded with (prisma/tenants/bootstrap.ts) is
// edited through these — ADR-0010: nothing here is an enum.

export interface StageInput {
  name?: string;
  isHydro?: boolean;
  isTerminal?: boolean;
  schedulesNext?: boolean;
  hasProgress?: boolean;
  instructions?: string | null;
  checklist?: string[];
  minPhotos?: number;
  /** The milestone the customer sees in the portal. Null clears it. */
  customerLabel?: string | null;
}

export interface JobStageFull extends JobStage {
  schedulesNext: boolean;
  customerLabel: string | null;
}

export const stagesApi = {
  list: () => request<JobStageFull[]>('/jobs/stages'),
  /** Appended to the end; reorder to place it. */
  create: (input: StageInput & { name: string }) =>
    request<JobStageFull>('/jobs/stages', { method: 'POST', body: JSON.stringify(input) }),
  update: (id: string, input: StageInput) =>
    request<JobStageFull>(`/jobs/stages/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  /** Refused once any job has history at the stage. */
  remove: (id: string) => request<{ deleted: boolean }>(`/jobs/stages/${id}`, { method: 'DELETE' }),
  /** Every stage id, in the new order. */
  reorder: (ids: string[]) =>
    request<JobStageFull[]>('/jobs/stages/reorder', { method: 'POST', body: JSON.stringify({ ids }) }),
};

export interface ServiceRow {
  id: string;
  name: string;
  description: string | null;
  defaultDisposition: 'BILL' | 'COVERED_BY_PO' | 'NOT_BILLED';
  isActive: boolean;
  _count: { lines: number; quoteLines: number };
}

export interface ServiceInput {
  name?: string;
  description?: string | null;
  defaultDisposition?: ServiceRow['defaultDisposition'];
  isActive?: boolean;
}

export const servicesApi = {
  list: (includeInactive = true) => request<ServiceRow[]>(`/services${includeInactive ? '?includeInactive=true' : ''}`),
  create: (input: ServiceInput & { name: string }) =>
    request<ServiceRow>('/services', { method: 'POST', body: JSON.stringify(input) }),
  update: (id: string, input: ServiceInput) =>
    request<ServiceRow>(`/services/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  /** Refused once a charge or quote line names it; retire instead. */
  remove: (id: string) => request<{ deleted: boolean }>(`/services/${id}`, { method: 'DELETE' }),
};

export interface LaborRateRow {
  id: string;
  userId: string | null;
  roleId: string | null;
  costRate: string;
  billRate: string;
  user: { id: string; firstName: string; lastName: string } | null;
  role: { id: string; name: string } | null;
}

export const laborRatesApi = {
  list: () => request<LaborRateRow[]>('/labor/rates'),
  /** Upserts by scope: both ids null is the shop rate. */
  set: (input: { userId?: string | null; roleId?: string | null; costRate: string; billRate: string }) =>
    request<LaborRateRow>('/labor/rates', { method: 'POST', body: JSON.stringify(input) }),
  update: (id: string, input: { costRate: string; billRate: string }) =>
    request<LaborRateRow>(`/labor/rates/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  remove: (id: string) => request<{ deleted: boolean }>(`/labor/rates/${id}`, { method: 'DELETE' }),
};

export interface CaptionTemplateRow extends CaptionTemplate {
  isActive: boolean;
}

export const captionTemplatesApi = {
  list: () => request<CaptionTemplateRow[]>('/caption-templates?includeInactive=true'),
  create: (input: { text: string; stageId?: string | null; position?: number }) =>
    request<CaptionTemplateRow>('/caption-templates', { method: 'POST', body: JSON.stringify(input) }),
  update: (id: string, input: { text?: string; stageId?: string | null; position?: number; isActive?: boolean }) =>
    request<CaptionTemplateRow>(`/caption-templates/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
};

export type StoreroomType = 'MAIN' | 'SATELLITE' | 'TRUCK' | 'JOBSITE' | 'QUARANTINE' | 'CONSIGNMENT';
export type LocationKind = 'AISLE' | 'RACK' | 'SHELF' | 'BIN' | 'ROW' | 'YARD' | 'CONEX' | 'STAGING';

export interface StorageLocationRow {
  id: string;
  storeroomId: string;
  parentId: string | null;
  code: string;
  path: string;
  kind: LocationKind;
  barcode: string | null;
  isPickable: boolean;
  capacityNote: string | null;
}

export interface StoreroomRow {
  id: string;
  code: string;
  name: string;
  type: StoreroomType;
  siteId: string | null;
  isActive: boolean;
  site: { id: string; code: string; name: string } | null;
  locations: StorageLocationRow[];
  _count: { locations: number };
}

export interface LocationInput {
  code?: string;
  kind?: LocationKind;
  barcode?: string | null;
  isPickable?: boolean;
  capacityNote?: string | null;
}

export const storeroomsApi = {
  list: () => request<StoreroomRow[]>('/storerooms'),
  create: (input: { code: string; name: string; type: StoreroomType; siteId?: string | null }) =>
    request<StoreroomRow>('/storerooms', { method: 'POST', body: JSON.stringify(input) }),
  update: (id: string, input: { name?: string; type?: StoreroomType; siteId?: string | null; isActive?: boolean }) =>
    request<StoreroomRow>(`/storerooms/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  /** Refused once stock or ledger history sits in it; retire instead. */
  remove: (id: string) => request<{ deleted: boolean }>(`/storerooms/${id}`, { method: 'DELETE' }),
  createLocation: (input: LocationInput & { storeroomId: string; parentId?: string | null; code: string; kind: LocationKind }) =>
    request<StorageLocationRow>('/storerooms/locations', { method: 'POST', body: JSON.stringify(input) }),
  /** Recoding rewrites the path of the location and everything under it. */
  updateLocation: (id: string, input: LocationInput) =>
    request<StorageLocationRow>(`/storerooms/locations/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
};

export type AssetStatus = 'UP' | 'DOWN' | 'MAINTENANCE' | 'RETIRED';

export interface AssetRow {
  id: string;
  code: string;
  name: string;
  make: string | null;
  model: string | null;
  serialNumber: string | null;
  isRental: boolean;
  status: AssetStatus;
  statusReason: string | null;
  location: string | null;
  workOrders: { id: string; summary: string; status: string }[];
}

export const assetsApi = {
  list: (includeRetired = true) => request<AssetRow[]>(`/assets${includeRetired ? '?includeRetired=true' : ''}`),
  create: (input: { code: string; name: string; make?: string | null; model?: string | null; serialNumber?: string | null; isRental?: boolean; location?: string | null }) =>
    request<AssetRow>('/assets', { method: 'POST', body: JSON.stringify(input) }),
  /** A reason is required for anything but UP; DOWN notifies whoever subscribes. */
  setStatus: (id: string, input: { status: AssetStatus; reason?: string | null }) =>
    request<AssetRow>(`/assets/${id}/status`, { method: 'POST', body: JSON.stringify(input) }),
};

export interface NotificationDefaults {
  events: { event: string; label: string; description: string }[];
  roles: { id: string; name: string; isAdmin: boolean; subscriptions: { event: string; channel: 'EMAIL' | 'SMS' }[] }[];
}

export const notificationDefaultsApi = {
  list: () => request<NotificationDefaults>('/notifications/defaults'),
  set: (roleId: string, input: { event: string; channel: 'EMAIL' | 'SMS'; enabled: boolean }) =>
    request<{ enabled: boolean }>(`/notifications/defaults/${roleId}`, { method: 'PUT', body: JSON.stringify(input) }),
};

// ── Platform console ────────────────────────────────────────────────────────
//
// A second, independent session (docs/adr/0011-platform-administration.md):
// its own token store, its own refresh path and cookie, so a platform
// administrator can keep the console open while inside a shop. The request
// helpers mirror the tenant ones above rather than parameterising them —
// two short functions beat one with a mode flag.

let inFlightPlatformRefresh: Promise<boolean> | null = null;
let onPlatformAuthLost: (() => void) | null = null;
export function setPlatformAuthLostHandler(handler: () => void): void {
  onPlatformAuthLost = handler;
}

async function platformRawRequest(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has('content-type')) headers.set('content-type', 'application/json');
  const token = platformTokenStore.get();
  if (token) headers.set('authorization', `Bearer ${token}`);
  if (isNativePlatform()) headers.set('x-client-platform', 'capacitor');
  return fetch(`${API_BASE_URL}${path}`, { ...init, headers, credentials: 'include' });
}

async function attemptPlatformRefresh(): Promise<boolean> {
  inFlightPlatformRefresh ??= (async () => {
    try {
      const response = await platformRawRequest('/platform/auth/refresh', {
        method: 'POST',
        body: JSON.stringify({ refreshToken: platformRefreshTokenStore.get() }),
      });
      if (!response.ok) return false;
      const data = (await response.json()) as PlatformAuthResponse;
      platformTokenStore.set(data.accessToken);
      platformRefreshTokenStore.set(data.refreshToken);
      platformSessionHint.set(true);
      return true;
    } catch {
      return false;
    } finally {
      queueMicrotask(() => {
        inFlightPlatformRefresh = null;
      });
    }
  })();
  return inFlightPlatformRefresh;
}

export async function platformRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response = await platformRawRequest(path, init);
  if (response.status === 401 && !path.startsWith('/platform/auth/refresh')) {
    if (await attemptPlatformRefresh()) {
      response = await platformRawRequest(path, init);
    } else {
      platformTokenStore.set(null);
      platformRefreshTokenStore.set(undefined);
      platformSessionHint.set(false);
      onPlatformAuthLost?.();
      throw await parseError(response);
    }
  }
  if (!response.ok) throw await parseError(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

function adoptPlatformSession(data: PlatformAuthResponse): PlatformAuthResponse {
  platformTokenStore.set(data.accessToken);
  platformRefreshTokenStore.set(data.refreshToken);
  platformSessionHint.set(true);
  return data;
}

export const platformAuthApi = {
  login: async (input: PlatformLoginInput) =>
    adoptPlatformSession(await platformRequest<PlatformAuthResponse>('/platform/auth/login', { method: 'POST', body: JSON.stringify(input) })),

  me: () => platformRequest<PlatformUserRecord>('/platform/auth/me'),

  logout: async () => {
    try {
      await platformRequest<void>('/platform/auth/logout', {
        method: 'POST',
        body: JSON.stringify({ refreshToken: platformRefreshTokenStore.get() }),
      });
    } finally {
      platformTokenStore.set(null);
      platformRefreshTokenStore.set(undefined);
      platformSessionHint.set(false);
    }
  },

  /** Silent restore on load. Never throws. */
  restore: async (): Promise<PlatformUserRecord | null> => {
    if (!platformSessionHint.exists()) return null;
    if (!(await attemptPlatformRefresh())) return null;
    try {
      return await platformRequest<PlatformUserRecord>('/platform/auth/me');
    } catch {
      return null;
    }
  },
};

export const platformApi = {
  listTenants: () => platformRequest<PlatformTenantSummary[]>('/platform/tenants'),
  getTenant: (id: string) => platformRequest<PlatformTenantRecord>(`/platform/tenants/${id}`),
  /** The administrator's temporary password comes back once and is never shown again. */
  createTenant: (input: CreateTenantInput) =>
    platformRequest<{ tenant: PlatformTenantRecord; admin: { email: string }; temporaryPassword: string }>(
      '/platform/tenants', { method: 'POST', body: JSON.stringify(input) },
    ),
  updateTenant: (id: string, input: PlatformUpdateTenantInput) =>
    platformRequest<PlatformTenantRecord>(`/platform/tenants/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  /** Refused unless `confirmSlug` is the shop's slug, exactly. */
  deleteTenant: (id: string, confirmSlug: string) =>
    platformRequest<{ deleted: true; slug: string }>(`/platform/tenants/${id}`, { method: 'DELETE', body: JSON.stringify({ confirmSlug }) }),
  /** A 15-minute shop session as platform support; see api.adoptTenantSession. */
  enterTenant: (id: string) =>
    platformRequest<EnterTenantResponse>(`/platform/tenants/${id}/enter`, { method: 'POST', body: '{}' }),
};

/** The shop's own view of who from HXIQ has been inside it. */
export const platformAccessApi = {
  list: () => request<PlatformAccessRecord[]>('/tenant/platform-access'),
};

// ── Counter tickets ─────────────────────────────────────────────────────────

import type { TicketInput } from '@hxiq/shared';

/** What a lookup carries for the receipt line's defaults (merged into LookupItem). */
export interface LookupItem {
  lastPurchaseDate: string | null;
  /** The item's default markup as a rate ("0.25"); the counter offers it as the suggested markup. */
  defaultMarkupPct: string;
  /** The supplier flagged preferred, else the most recently quoted one. */
  preferredSupplier: {
    id: string;
    name: string;
    supplierPartNumber: string;
    lastQuotedPrice: string | null;
    lastQuotedAt: string | null;
  } | null;
  /** The fullest location, else where it was last received. Null if never stocked. */
  usualLocationId: string | null;
}

export interface ReceiptLineResult {
  transactionId: string;
  balanceAfter: string;
}

export type TicketResult =
  | { mode: 'ISSUE'; lines: IssueResult[] }
  | { mode: 'RECEIVE'; lines: ReceiptLineResult[] };

export const inventoryTicketApi = {
  /** Every line in one transaction, or none. A refusal names its line. */
  post: (body: TicketInput) =>
    request<TicketResult>('/inventory/tickets', { method: 'POST', body: JSON.stringify(body) }),
};

// ── Catalog detail and suppliers ─────────────────────────────────────────────

export interface CatalogSearchParams {
  q?: string;
  categoryId?: string;
  status?: string;
  belowMin?: boolean;
  sort?: 'name' | 'partNumber' | 'category' | 'averageCost' | 'available';
  dir?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

export interface LedgerQuery {
  locationId?: string;
  /** YYYY-MM-DD, inclusive. */
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
}

export interface CatalogSpec {
  id: string;
  key: string;
  value: string;
  unit: string | null;
}

export interface StockBalanceRow {
  id: string;
  locationId: string;
  lotId: string | null;
  quantityOnHand: string;
  quantityReserved: string;
  quantityAvailable: string | null;
  unitCost: string | null;
  ownershipType: 'OWNED' | 'CUSTOMER' | 'CONSIGNED';
  ownerCustomerId: string | null;
  ownerCustomer: { id: string; name: string } | null;
  /** Location overrides; null inherits the catalog default. */
  binMin: string | null;
  binMax: string | null;
  lastCountedAt: string | null;
  location: { id: string; code: string; path: string; storeroom: { id: string; code: string; name: string } };
  lot: { id: string; lotNumber: string; heatNumber: string | null } | null;
}

export interface SupplierItemRow {
  id: string;
  supplierId: string;
  supplierPartNumber: string;
  purchaseUomId: string | null;
  lastQuotedPrice: string | null;
  lastQuotedAt: string | null;
  leadTimeDays: number | null;
  minimumOrderQty: string | null;
  orderMultiple: string | null;
  isPreferred: boolean;
  supplier: { id: string; name: string; isActive: boolean };
  purchaseUom: { id: string; code: string; name: string } | null;
}

export interface CrossReferenceRow {
  id: string;
  type: 'IDENTICAL' | 'EQUIVALENT' | 'ALTERNATE' | 'SUPERSEDES' | 'SUPERSEDED_BY';
  note: string | null;
  other: { id: string; name: string; companyPartNumber: string | null; manufacturerPartNumber: string | null };
}

/** An active hold on this item, for one job. */
export interface ReservationRow {
  id: string;
  jobId: string;
  job: { id: string; projectNumber: string; name: string };
  locationId: string;
  lotId: string | null;
  quantity: string;
  quantityIssued: string;
  remaining: string;
  expiresAt: string | null;
  note: string | null;
}

export interface CatalogItemDetail {
  id: string;
  name: string;
  description: string | null;
  companyPartNumber: string | null;
  previousPartNumbers: string[];
  manufacturerPartNumber: string | null;
  manufacturerId: string | null;
  categoryId: string | null;
  stockUomId: string;
  status: 'DRAFT' | 'ACTIVE' | 'OBSOLETE' | 'SUPERSEDED';
  isSerialized: boolean;
  isLotTracked: boolean;
  shelfLifeDays: number | null;
  isHazmat: boolean;
  defaultMarkupPct: string;
  minMarginPct: string;
  averageCost: string | null;
  lastPurchaseCost: string | null;
  lastPurchaseDate: string | null;
  lastIssuedDate: string | null;
  reorderPolicy: 'NONE' | 'MIN_MAX';
  defaultBinMin: string | null;
  defaultBinMax: string | null;
  countClass: 'A' | 'B' | 'C' | null;
  createdAt: string;
  updatedAt: string;
  category: { id: string; name: string; code: string; path: string; specTemplate: unknown } | null;
  manufacturer: { id: string; name: string } | null;
  stockUom: { id: string; code: string; name: string };
  specs: CatalogSpec[];
  supplierItems: SupplierItemRow[];
  stockBalances: StockBalanceRow[];
  crossReferences: CrossReferenceRow[];
  reservations: ReservationRow[];
  /** What stands between this item and deletion. */
  _count: { transactions: number; jobIssueLines: number };
}

export interface SupplierRow {
  id: string;
  name: string;
  emailDomains: string[];
  contactEmail: string | null;
  phone: string | null;
  isActive: boolean;
  _count: { supplierItems: number; documents: number };
}

export interface SupplierInput {
  name: string;
  contactEmail?: string | null;
  phone?: string | null;
  emailDomains?: string[];
  isActive?: boolean;
}

export const suppliersApi = {
  list: () => request<SupplierRow[]>('/suppliers'),
  /** Returns the existing supplier of that name rather than a duplicate. */
  create: (input: SupplierInput) =>
    request<SupplierRow & { created: boolean }>('/suppliers', { method: 'POST', body: JSON.stringify(input) }),
  update: (id: string, input: Partial<SupplierInput>) =>
    request<SupplierRow>(`/suppliers/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
};

// ── Storeroom summary and counting (appended; see features/inventory and features/counting) ──

export interface StoreroomSummaryRow {
  id: string;
  code: string;
  name: string;
  type: string;
  site: { id: string; name: string; timeZone: string } | null;
  locations: number;
  itemsWithStock: number;
  /** Sum of on hand × the item's average cost, as a decimal string. */
  onHandValue: string;
  movementsToday: number;
  itemsBelowMinimum: number;
  lastCount: { id: string; reference: string; postedAt: string } | null;
}

export interface ReorderRow {
  balanceId: string;
  catalogItemId: string;
  name: string;
  companyPartNumber: string | null;
  locationId: string;
  locationPath: string;
  storeroomId: string | null;
  storeroomCode: string | null;
  available: string;
  effectiveMin: string;
  effectiveMax: string | null;
  suggestedOrderQty: string;
  preferredSupplier: { id: string; name: string; supplierPartNumber: string } | null;
}

export interface StoreroomSummary {
  storerooms: StoreroomSummaryRow[];
  reorder: ReorderRow[];
}

export const storeroomSummaryApi = {
  get: () => request<StoreroomSummary>('/inventory/storerooms/summary'),
};

export interface CycleCountListRow extends CycleCountRow {
  startedAt: string | null;
  startedBy: { id: string; name: string } | null;
  storeroom: { id: string; code: string; name: string };
  /** Lines counted or skipped, over the sheet. */
  countedLines: number;
  totalLines: number;
}

export interface CycleCountSheetLine extends CycleCountLineRow {
  location: { id: string; code: string; path: string; barcode: string | null };
  countedBy: { id: string; name: string } | null;
  /** Could not be counted. Does not block posting. */
  skipped: boolean;
  skipNote: string | null;
  skippedAt: string | null;
  skippedBy: { id: string; name: string } | null;
  /** When this balance was last counted, from the stock record. */
  lastCountedAt: string | null;
}

export interface CycleCountSheet extends CycleCountRow {
  startedAt: string | null;
  startedBy: { id: string; name: string } | null;
  reviewedBy: { id: string; name: string } | null;
  storeroom: { id: string; code: string; name: string };
  /** A line varies materially; posting needs an approval note and inventory:approve_variance. */
  approvalNoteRequired: boolean;
  varianceSummary: { varyingLines: number; totalAbsoluteVariance: string };
  lines: CycleCountSheetLine[];
}

export const countingApi = {
  list: (filter: { storeroomId?: string; status?: string } = {}) =>
    request<CycleCountListRow[]>(`/counts${qs(filter)}`),
  get: (id: string) => request<CycleCountSheet>(`/counts/${id}`),
  addLine: (id: string, body: { catalogItemId: string; locationId: string; countedQuantity: string; note?: string | null }) =>
    request<{ lineId: string; recountRequested: boolean; message: string | null; added: true }>(
      `/counts/${id}/lines/add`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  skipLine: (id: string, lineId: string, note: string) =>
    request<{ lineId: string; skipped: true }>(`/counts/${id}/lines/${lineId}/skip`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),
  cancel: (id: string, reason: string) =>
    request<{ reference: string; status: 'CANCELLED' }>(`/counts/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  post: (id: string, body: { approvalNote: string | null }) =>
    request<{ reference: string; linesCounted: number; linesSkipped: number; adjustmentsPosted: number; requiredApproval: boolean }>(
      `/counts/${id}/post`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
};

// ── The in-app inbox: the bell ──────────────────────────────────────────────

export type InboxNotification = import('@hxiq/shared').InboxNotification;

export const inboxApi = {
  list: (opts: { unreadOnly?: boolean; page?: number; limit?: number } = {}) => {
    const params = new URLSearchParams();
    if (opts.unreadOnly) params.set('unread', '1');
    if (opts.page) params.set('page', String(opts.page));
    if (opts.limit) params.set('limit', String(opts.limit));
    const qs = params.toString();
    return request<Paged<InboxNotification>>(`/notifications/inbox${qs ? `?${qs}` : ''}`);
  },
  unreadCount: () => request<{ unread: number }>('/notifications/inbox/count'),
  /** Omit `ids` to mark everything read. */
  markRead: (ids?: string[]) =>
    request<{ marked: number }>('/notifications/inbox/read', { method: 'POST', body: JSON.stringify(ids ? { ids } : {}) }),
};

// ── Global search ───────────────────────────────────────────────────────────

/** One row in the shell's search dialog: what to show and where it goes. */
export interface SearchHit {
  id: string;
  primary: string;
  secondary: string;
  route: string;
}
export interface SearchSection {
  hits: SearchHit[];
  /** Of the whole match, not just the hits returned. */
  total: number;
}
export type SearchSectionName = 'jobs' | 'items' | 'customers' | 'equipment';
/** A section the caller may not see is absent, not empty. */
export type SearchResponse = Partial<Record<SearchSectionName, SearchSection>>;

export const searchApi = {
  search: (q: string, limit = 8) =>
    request<SearchResponse>(`/search?q=${encodeURIComponent(q)}&limit=${limit}`),
};

// ── Customer portal: the shop's side ────────────────────────────────────────
// docs/features/customer-portal.md

import type {
  ChangeOrderRequestInput, ChangeOrderRequestRecord, PortalAccessRecord, PortalInviteResult,
} from '@hxiq/shared';

export const portalAccessApi = {
  /** Every contact of the customer that has ever been invited, keyed by contact id. */
  forCustomer: (customerId: string) => request<Record<string, PortalAccessRecord>>(`/customers/${customerId}/portal-access`),
  /** The link comes back once; it is stored only as a hash. */
  invite: (contactId: string) => request<PortalInviteResult>(`/customers/contacts/${contactId}/portal`, { method: 'POST' }),
  revoke: (contactId: string) => request<PortalAccessRecord>(`/customers/contacts/${contactId}/portal`, { method: 'DELETE' }),
};

export const customerFacingApi = {
  /** Expected back and the status line. Blank clears either; each change is logged. */
  update: (jobId: string, input: { promisedDate?: string | null; customerStatus?: string | null }) =>
    request<{ id: string; promisedDate: string | null; customerStatus: string | null; customerStatusAt: string | null }>(
      `/jobs/${jobId}/customer`, { method: 'PATCH', body: JSON.stringify(input) },
    ),
  share: (photoId: string, shared: boolean) =>
    request<{ id: string; sharedAt: string | null }>(`/photos/${photoId}/share`, { method: 'PATCH', body: JSON.stringify({ shared }) }),
  /** Renders the final report, files it as a FINAL_REPORT document, shares its photos. */
  issueFinalReport: (jobId: string, photoIds: string[]) =>
    request<JobDocument>(`/jobs/${jobId}/final-report/issue`, { method: 'POST', body: JSON.stringify({ photoIds }) }),
};

export const changeOrdersApi = {
  list: (orderId: string) => request<ChangeOrderRequestRecord[]>(`/orders/${orderId}/change-orders`),
  request: (orderId: string, input: ChangeOrderRequestInput) =>
    request<ChangeOrderRequestRecord>(`/orders/${orderId}/change-orders`, { method: 'POST', body: JSON.stringify(input) }),
  withdraw: (id: string) => request<ChangeOrderRequestRecord>(`/change-orders/${id}/withdraw`, { method: 'POST' }),
};

// ── Customer portal: the customer's session ─────────────────────────────────
//
// A third, independent session (ADR-0013): its own token store, refresh path
// and cookie. The helpers mirror the platform console's rather than
// parameterising the shop's — two short functions beat one with a mode flag.

import type {
  PortalAuthResponse, PortalEquipmentDetail, PortalEquipmentRow, PortalHome, PortalInvitePreview,
  PortalLoginInput, PortalOrder, PortalQuote, PortalUserRecord, PortalVisitDetail,
} from '@hxiq/shared';
import { portalRefreshTokenStore, portalSessionHint, portalTokenStore } from './token-store';

let inFlightPortalRefresh: Promise<boolean> | null = null;
let onPortalAuthLost: (() => void) | null = null;
export function setPortalAuthLostHandler(handler: () => void): void {
  onPortalAuthLost = handler;
}

async function portalRawRequest(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  const isFormData = typeof FormData !== 'undefined' && init.body instanceof FormData;
  if (init.body && !isFormData && !headers.has('content-type')) headers.set('content-type', 'application/json');
  const token = portalTokenStore.get();
  if (token) headers.set('authorization', `Bearer ${token}`);
  if (isNativePlatform()) headers.set('x-client-platform', 'capacitor');
  return fetch(`${API_BASE_URL}${path}`, { ...init, headers, credentials: 'include' });
}

async function attemptPortalRefresh(): Promise<boolean> {
  inFlightPortalRefresh ??= (async () => {
    try {
      const response = await portalRawRequest('/portal/auth/refresh', {
        method: 'POST',
        body: JSON.stringify({ refreshToken: portalRefreshTokenStore.get() }),
      });
      if (!response.ok) return false;
      const data = (await response.json()) as PortalAuthResponse;
      portalTokenStore.set(data.accessToken);
      portalRefreshTokenStore.set(data.refreshToken);
      portalSessionHint.set(true);
      return true;
    } catch {
      return false;
    } finally {
      queueMicrotask(() => {
        inFlightPortalRefresh = null;
      });
    }
  })();
  return inFlightPortalRefresh;
}

export async function portalRequestRaw(path: string, init: RequestInit = {}): Promise<Response> {
  let response = await portalRawRequest(path, init);
  if (response.status === 401 && !path.startsWith('/portal/auth/')) {
    if (await attemptPortalRefresh()) {
      response = await portalRawRequest(path, init);
    } else {
      portalTokenStore.set(null);
      portalRefreshTokenStore.set(undefined);
      portalSessionHint.set(false);
      onPortalAuthLost?.();
      throw await parseError(response);
    }
  }
  if (!response.ok) throw await parseError(response);
  return response;
}

export async function portalRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await portalRequestRaw(path, init);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

function adoptPortalSession(data: PortalAuthResponse): PortalAuthResponse {
  portalTokenStore.set(data.accessToken);
  portalRefreshTokenStore.set(data.refreshToken);
  portalSessionHint.set(true);
  return data;
}

export const portalAuthApi = {
  login: async (input: PortalLoginInput) =>
    adoptPortalSession(await portalRequest<PortalAuthResponse>('/portal/auth/login', { method: 'POST', body: JSON.stringify(input) })),
  invite: (token: string) => portalRequest<PortalInvitePreview>(`/portal/auth/invite/${encodeURIComponent(token)}`),
  acceptInvite: async (token: string, password: string) =>
    adoptPortalSession(await portalRequest<PortalAuthResponse>(`/portal/auth/invite/${encodeURIComponent(token)}`, {
      method: 'POST', body: JSON.stringify({ password }),
    })),
  logout: async () => {
    try {
      await portalRequest<void>('/portal/auth/logout', { method: 'POST', body: JSON.stringify({ refreshToken: portalRefreshTokenStore.get() }) });
    } finally {
      portalTokenStore.set(null);
      portalRefreshTokenStore.set(undefined);
      portalSessionHint.set(false);
    }
  },
  /** Silent restore on load. Never throws. */
  restore: async (): Promise<PortalUserRecord | null> => {
    if (!portalSessionHint.exists()) return null;
    if (!(await attemptPortalRefresh())) return null;
    try {
      return await portalRequest<PortalUserRecord>('/portal/auth/me');
    } catch {
      return null;
    }
  },
};

export const portalApi = {
  home: () => portalRequest<PortalHome>('/portal/home'),
  equipment: () => portalRequest<PortalEquipmentRow[]>('/portal/equipment'),
  unit: (id: string) => portalRequest<PortalEquipmentDetail>(`/portal/equipment/${id}`),
  visit: (id: string) => portalRequest<PortalVisitDetail>(`/portal/visits/${id}`),
  quotes: () => portalRequest<PortalQuote[]>('/portal/quotes'),
  quote: (id: string) => portalRequest<PortalQuote>(`/portal/quotes/${id}`),
  approveQuote: (id: string, poNumber: string | null) =>
    portalRequest<PortalQuote>(`/portal/quotes/${id}/approve`, { method: 'POST', body: JSON.stringify({ poNumber }) }),
  declineQuote: (id: string, reason: string) =>
    portalRequest<PortalQuote>(`/portal/quotes/${id}/decline`, { method: 'POST', body: JSON.stringify({ reason }) }),
  order: (id: string) => portalRequest<PortalOrder>(`/portal/orders/${id}`),
  sendPo: (id: string, number: string, file: File) => {
    const form = new FormData();
    form.append('number', number);
    form.append('file', file);
    return portalRequest<PortalOrder>(`/portal/orders/${id}/po`, { method: 'POST', body: form });
  },
  approveChangeOrder: (id: string, customerReference: string | null) =>
    portalRequest<PortalOrder>(`/portal/change-orders/${id}/approve`, { method: 'POST', body: JSON.stringify({ customerReference }) }),
  declineChangeOrder: (id: string, reason: string) =>
    portalRequest<PortalOrder>(`/portal/change-orders/${id}/decline`, { method: 'POST', body: JSON.stringify({ reason }) }),
  /** Bytes for an <img> or a new tab: the bearer token cannot ride on either. */
  blob: async (path: string): Promise<Blob> => (await portalRequestRaw(path)).blob(),
  photoPath: (id: string, size: 'thumb' | 'full') => `/portal/photos/${id}/file${size === 'thumb' ? '?size=thumb' : ''}`,
  documentPath: (id: string) => `/portal/documents/${id}/file`,
  invoicePath: (orderId: string) => `/portal/orders/${orderId}/invoice.pdf`,
};
